import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Lock, Calendar } from "lucide-react";

/**
 * Dialog that asks for a date (completion date) and optionally the admin
 * password when the budget is finalized.
 */
export default function CompleteItemDialog({
  open,
  onOpenChange,
  requirePassword = false,
  initialDate = "",
  mode = "complete", // complete | reopen | change-date
  onConfirm,
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(initialDate || today);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(initialDate || today);
      setPassword("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialDate]);

  const titles = {
    complete: "Concluir item",
    reopen: "Reabrir item",
    "change-date": "Alterar data de conclusão",
  };
  const btn = {
    complete: "Marcar como concluído",
    reopen: "Reabrir item",
    "change-date": "Salvar nova data",
  };

  const handleConfirm = async () => {
    if (mode !== "reopen" && !date) return;
    if (requirePassword && !password.trim()) return;
    setSaving(true);
    try {
      await onConfirm?.({ date, password });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="complete-item-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1e3a8a]">
            {requirePassword ? (
              <Lock className="w-5 h-5 text-amber-600" />
            ) : (
              <Calendar className="w-5 h-5 text-[#7c3aed]" />
            )}
            {titles[mode]}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {mode !== "reopen" && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Data de conclusão
              </Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="input-complete-date"
              />
            </div>
          )}
          {requirePassword && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-amber-700 flex items-center gap-1">
                <Lock className="w-3 h-3" /> Senha do administrador
              </Label>
              <PasswordInput
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite a senha"
                autoFocus
                data-testid="input-admin-password"
              />
              <p className="text-[11px] text-amber-700 mt-1">
                Este orçamento está finalizado. É necessária a senha de administrador para alterar itens.
              </p>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={handleConfirm}
            disabled={saving || (mode !== "reopen" && !date) || (requirePassword && !password.trim())}
            className={mode === "reopen" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[#7c3aed] hover:bg-[#6d28d9] text-white"}
            data-testid="btn-confirm-complete"
          >
            {saving ? "Salvando..." : btn[mode]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
